import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "../context/ThemeContext";
import DOMPurify from "isomorphic-dompurify";
import { getNewsBroadcasts } from "../api/emailAPI";
import { PageContainer, PageHeading, SectionHeading, Card } from "../components/ui";

/**
 * The family News page. Until 2026-10-11 it was only a fixed welcome note:
 * the club's news (broadcasts sent with "show as news") appeared on the
 * dashboard but never here. It now lists them under the note.
 *
 * `embedded` renders it as a section inside another page (the dashboard's
 * attendance card) instead of as a routed page with its own frame and H1.
 */

interface NewsProps {
  embedded?: boolean;
}

const News: React.FC<NewsProps> = ({ embedded = false }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation('dashboard');
  const [settings, setSettings] = useState<any>(null);
  const [news, setNews] = useState<any[]>([]);
  const [newsState, setNewsState] = useState<"loading" | "ready" | "failed">("loading");
  // HELPER
  const storedSettings = localStorage.getItem("settings");
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const xTenantId = pathParts[0] || null;

  useEffect(() => {
    if (storedSettings) {
      setSettings(JSON.parse(storedSettings));
    }
  }, [storedSettings]);

  useEffect(() => {
    let live = true;
    getNewsBroadcasts(localStorage.getItem("authToken"), { limit: 20 })
      .then((res: any) => {
        if (!live) return;
        setNews(res?.success && Array.isArray(res?.data) ? res.data : []);
        setNewsState(res?.success ? "ready" : "failed");
      })
      .catch(() => {
        if (live) setNewsState("failed");
      });
    return () => {
      live = false;
    };
  }, []);

  const content = (
    <>

      <div className={`${isDarkMode ? 'bg-orange-950 border-orange-800' : 'bg-orange-100 border-orange-300'} border-2 rounded-md p-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        <p className="mb-2">
          {t('news.welcomeMessage')}{" "}
          <span className={`font-semibold ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>
            {settings?.business_name}
          </span>
          . {t('news.manageAthletes')}{" "}
          <Link
            to={`/${xTenantId}/member/manage-athlete`}
            className={`underline font-medium ${isDarkMode ? 'text-orange-300 hover:text-orange-200' : 'text-orange-600 hover:text-orange-700'}`}
          >
            {t('news.athleteEnrollments')}
          </Link>
          {t('news.viewInvoices')}{" "}
          <Link
            to={`/${xTenantId}/member/view-billing`}
            className={`underline font-medium ${isDarkMode ? 'text-orange-300 hover:text-orange-200' : 'text-orange-600 hover:text-orange-700'}`}
          >
            {t('news.invoices')}
          </Link>{" "}
          {t('news.makePayments')}{" "}
          <Link
            to={`/${xTenantId}/member/update-card`}
            className={`underline font-medium ${isDarkMode ? 'text-orange-300 hover:text-orange-200' : 'text-orange-600 hover:text-orange-700'}`}
          >
            {t('news.paymentMethods')}
          </Link>
          {t('news.helpMessage')}{" "}
          <Link
            to={`/${xTenantId}/member/contact`}
            className={`underline font-medium ${isDarkMode ? 'text-orange-300 hover:text-orange-200' : 'text-orange-600 hover:text-orange-700'}`}
          >
            {t('news.contactUs')}
          </Link>
          .
        </p>
        <p className="mt-4">{t('news.thankYou')}</p>
      </div>

      <section aria-label="Club news" className="mt-6 space-y-3">
        {newsState === "loading" && (
          <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>Loading news...</p>
        )}
        {newsState === "failed" && (
          <p role="alert" className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
            The club's news could not be loaded. Please try again later.
          </p>
        )}
        {newsState === "ready" && news.length === 0 && (
          <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>No news from the club yet.</p>
        )}
        {news.map((item: any) => (
          <Card
            role="article"
            key={item.id}
            padding={embedded ? "sm" : "md"}
            className={isDarkMode ? "text-gray-200" : "text-gray-800"}
          >
            <h3 className="font-semibold">{item.subject}</h3>
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {new Date(item.sent_at || item.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </p>
            <div
              className={`prose prose-sm max-w-none mt-2 ${isDarkMode ? "prose-invert" : ""}`}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.message || item.plain_text || "") }}
            />
          </Card>
        ))}
      </section>
    </>
  );

  if (embedded) {
    return (
      <div className="mb-4">
        <SectionHeading>{t('news.title')}</SectionHeading>
        {content}
      </div>
    );
  }

  return (
    <PageContainer>
      <PageHeading title={t('news.title')} />
      {content}
    </PageContainer>
  );
};

export default News;
